import { useState } from 'react';
import { Loader2, Lock, NotebookPen, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, useToast } from '@mustauto/ui/web';
import { formatApiError, INTERNAL_NOTES_MAX, normalizeInternalNotes } from '@mustauto/utils';
import { WhoCanHint } from '@/components/WhoCanHint';

/**
 * As anotações internas da OS, editáveis no próprio card. Antes o painel só as
 * mostrava na impressão, e elas só podiam ser escritas na abertura: quem
 * desmontava e achava outra peça danificada não tinha onde escrever (pedido de
 * cliente, out/2026). O app edita a mesma coluna, offline.
 *
 * O cliente nunca vê (Regra 7). Edita quem executa OS, até a OS ser entregue
 * ou cancelada — a API recusa fora disso, e aqui o card só mostra o texto.
 * Texto vazio apaga; o de antes fica no histórico da OS.
 */
export function OSInternalNotesCard({
  osId,
  internalNotes,
  locked,
  canExecute,
  lastEdit,
  onSave,
  isSaving,
}: {
  osId: string;
  internalNotes: string | null;
  /** OS entregue ou cancelada: a anotação virou registro. */
  locked: boolean;
  canExecute: boolean;
  /** Quem editou por último e quando — do histórico da OS. */
  lastEdit: { actorName: string; createdAt: string } | null;
  onSave: (internalNotes: string | null) => Promise<unknown>;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const editable = !locked && canExecute;
  // OS encerrada sem anotação: não há o que mostrar.
  if (locked && !internalNotes) return null;

  const start = () => {
    setValue(internalNotes ?? '');
    setEditing(true);
  };

  const save = async () => {
    const next = normalizeInternalNotes(value);
    if (next === normalizeInternalNotes(internalNotes)) {
      setEditing(false);
      return;
    }
    try {
      await onSave(next);
      setEditing(false);
      toast({
        type: 'success',
        title: next ? 'Anotação salva' : 'Anotação apagada',
        description: 'Fica no histórico da OS.',
      });
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível salvar a anotação',
        description: formatApiError(err, 'O que você escreveu continua aqui. Tente de novo.'),
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <NotebookPen size={16} aria-hidden />
            Anotações internas
          </CardTitle>
          {editable && !editing && internalNotes && (
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Pencil size={12} aria-hidden />
              Editar
            </button>
          )}
        </div>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock size={12} aria-hidden className="shrink-0" />
          Só a oficina vê. Sai na impressão só quando você marca “Incluir observações internas”.
        </p>
        {!locked && !canExecute && <WhoCanHint permission="os.executar" action="muda as anotações" />}
      </CardHeader>
      <CardContent className="space-y-2">
        {editing ? (
          <>
            <label htmlFor={`internal-notes-${osId}`} className="sr-only">
              Anotações internas
            </label>
            <textarea
              id={`internal-notes-${osId}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={INTERNAL_NOTES_MAX}
              rows={5}
              autoFocus
              placeholder="Ex.: ao desmontar, a bandeja esquerda está trincada. Precisa trocar."
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={isSaving}
                className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={isSaving}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
              >
                {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden />}
                Salvar
              </button>
            </div>
          </>
        ) : internalNotes ? (
          <p className="whitespace-pre-wrap break-words text-sm">{internalNotes}</p>
        ) : editable ? (
          <button
            type="button"
            onClick={start}
            className="w-full rounded-md border border-dashed px-3 py-3 text-left text-sm text-muted-foreground hover:border-primary hover:text-foreground"
          >
            Achou mais alguma coisa ao desmontar? <span className="font-medium text-primary">Anotar</span>
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhuma anotação.</p>
        )}
        {lastEdit && !editing && (
          <p className="text-xs text-muted-foreground">
            Última edição: {lastEdit.actorName},{' '}
            {new Date(lastEdit.createdAt).toLocaleString('pt-BR', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
